import type { CaptureResult } from "posthog-js";

const PRIVACY_KEY = "mpr.notice-product-only:v1";
type PrivacyStorage = Pick<Storage, "getItem" | "setItem">;

/** A privacy routing flag, NOT an attribution context, identity or consent. */
export function hasNoticeLink(value: string, depth = 0): boolean {
  if (depth > 3 || value.length > 16_384) return false;
  try {
    const url = new URL(value, "https://notice.invalid");
    if (url.searchParams.has("mpr_trace")) return true; // malformed links also stay private
    return ["next", "returnTo", "redirectTo"].some(key => {
      const next = url.searchParams.get(key);
      return next ? hasNoticeLink(next, depth + 1) : false;
    });
  } catch { return false; }
}

export function createNoticePrivacyScope(options: {
  location: () => string; storage: () => PrivacyStorage | null;
}) {
  let productOnly = false;
  return () => {
    if (productOnly) return true;
    try { productOnly = hasNoticeLink(options.location()); } catch { /* SSR/unavailable location */ }
    try {
      const storage = options.storage();
      productOnly ||= storage?.getItem(PRIVACY_KEY) === "1";
      if (productOnly) storage?.setItem(PRIVACY_KEY, "1");
    } catch { /* blocked storage must not block the app or forget the in-memory flag */ }
    return productOnly;
  };
}

// Keep suppression for the tab, including OAuth/provider returns without query
// context. It contains no user/trace IDs and never means "attribute this session".
export const isNoticeProductOnly = createNoticePrivacyScope({
  location: () => typeof window === "undefined" ? "" : window.location.href,
  storage: () => typeof window === "undefined" ? null : window.sessionStorage,
});

const PRIVATE_KEYS = new Set([
  "email", "phone", "telephone", "mobile", "contact", "name", "customername", "customeremail", "customerphone",
  "guardianname", "guardianphone", "caregivername", "petname", "dogname", "householdname", "invitename", "invitephone",
  "authorization", "accesstoken", "refreshtoken", "idtoken", "jwt", "billingkey", "billingissuetoken", "oauthcode",
  "code", "pgcode", "state", "nonce", "password", "secret", "token", "message", "pgmessage", "errormessage",
  "request", "response", "body", "payload",
]);
const keyName = (key: string) => key.replace(/[^a-z0-9]/gi, "").toLowerCase();
const urlKey = (key: string) => /(?:url|uri|href|referrer|path|next|returnto|redirectto)$/.test(keyName(key));

function safeUrl(value: string): string | undefined {
  try {
    if (value.startsWith("/") && !value.startsWith("//")) return new URL(value, "https://notice.invalid").pathname;
    const url = new URL(value);
    if (url.protocol !== "https:" && url.protocol !== "http:") return undefined;
    return `${url.origin}${url.pathname}`; // no query, fragment, username or password
  } catch { return undefined; }
}

/** Exact sensitive fields, not a blanket /id|token/ deletion. */
export function redactNoticeProperties(value: Record<string, unknown>, sdkToken?: string): Record<string, unknown> {
  function clean(input: unknown, depth: number, key = ""): unknown {
    if (depth > 8 || input === undefined) return undefined;
    if (Array.isArray(input)) return input.map(item => clean(item, depth + 1, key)).filter(item => item !== undefined);
    if (input && typeof input === "object") {
      const output: Record<string, unknown> = {};
      for (const [field, item] of Object.entries(input)) {
        // Only the SDK's exact public project token is retained, at its reserved
        // location. Customer-supplied tokens and tokens in $set are never kept.
        if (depth === 0 && field === "token" && sdkToken && item === sdkToken) { output.token = sdkToken; continue; }
        if (PRIVATE_KEYS.has(keyName(field))) continue;
        const next = clean(item, depth + 1, field);
        if (next !== undefined) output[field] = next;
      }
      return output;
    }
    if (typeof input === "string" && urlKey(key)) return safeUrl(input);
    return input;
  }
  return clean(value, 0) as Record<string, unknown>;
}

export function redactNoticeCapture(event: CaptureResult | null, sdkToken: string): CaptureResult | null {
  if (!event) return null;
  // Keep error blobs out of the notice funnel, but allow session replay now that
  // the product's configured collection scope includes notice-link sessions.
  if (event.event === "$exception") return null;
  return {
    ...event,
    properties: redactNoticeProperties(event.properties ?? {}, sdkToken),
    ...(event.$set ? { $set: redactNoticeProperties(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: redactNoticeProperties(event.$set_once) } : {}),
  };
}
